import {
  ANIMATION_TYPES,
  type AnimationSegment,
} from '../../project/ProjectDocument.js'
import type { KernelAnimationMode } from './sampleAnimation.js'

export type AnimationSegmentTimeline = ReadonlyArray<Readonly<AnimationSegment>>

/** Cumulative weight window over global eased progress 0…1. */
export interface AnimationSegmentWindow {
  readonly type: KernelAnimationMode
  readonly start: number
  readonly end: number
}

/** Compatibility view of the owning segment and its local progress. */
export interface AnimationSegmentSample {
  readonly type: KernelAnimationMode
  readonly progress: number
}

/**
 * Continuous timeline view. Both modes are sampled at `progress`; `blend`
 * crossfades from the preceding mode to the current one without restarting
 * the chaos-to-order journey at a segment boundary.
 */
export interface AnimationTimelineSample {
  readonly fromType: KernelAnimationMode
  readonly toType: KernelAnimationMode
  readonly blend: number
  readonly progress: number
}

function isKernelAnimationMode(value: unknown): value is KernelAnimationMode {
  return typeof value === 'string'
    && (ANIMATION_TYPES as readonly string[]).includes(value)
}

/**
 * Normalizes a persisted segment list into cumulative weight windows. Invalid
 * input returns null instead of partially accepting a corrupt timeline.
 */
export function resolveAnimationSegmentWindows(
  segments: AnimationSegmentTimeline | undefined,
): readonly AnimationSegmentWindow[] | null {
  if (segments === undefined || segments.length === 0) return null
  let totalWeight = 0
  for (const segment of segments) {
    if (
      segment === null
      || typeof segment !== 'object'
      || !isKernelAnimationMode(segment.type)
      || typeof segment.weight !== 'number'
      || !Number.isFinite(segment.weight)
      || segment.weight <= 0
    ) return null
    totalWeight += segment.weight
  }
  if (!Number.isFinite(totalWeight) || totalWeight <= 0) return null
  const windows: AnimationSegmentWindow[] = []
  let start = 0
  for (const segment of segments) {
    const end = start + segment.weight / totalWeight
    if (end > start) {
      windows.push({ type: segment.type, start, end })
      start = end
    }
  }
  const last = windows[windows.length - 1]
  if (last === undefined) return null
  windows[windows.length - 1] = { type: last.type, start: last.start, end: 1 }
  return windows
}

/**
 * Builds the editor timeline. The main Animation selector is a fixed first
 * phase with weight 1, so it remains meaningful whenever optional segments
 * are present.
 */
export function resolveAnimationTimelineWindows(
  baseType: KernelAnimationMode,
  segments: AnimationSegmentTimeline | undefined,
): readonly AnimationSegmentWindow[] | null {
  if (segments === undefined || segments.length === 0) return null
  if (!isKernelAnimationMode(baseType)) return null
  return resolveAnimationSegmentWindows([
    { type: baseType, weight: 1 },
    ...segments,
  ])
}

/**
 * Legacy segment-local mapper retained for portable API compatibility. New
 * playback uses `sampleAnimationTimeline`, which never restarts progress.
 */
export function sampleAnimationSegment(
  windows: readonly AnimationSegmentWindow[],
  progress: number,
): AnimationSegmentSample {
  const last = windows[windows.length - 1]
  if (last === undefined) {
    throw new RangeError('Animation segment timeline requires at least one window')
  }
  const clamped = Math.max(0, Math.min(1, progress))
  for (const window of windows) {
    if (clamped < window.end) {
      return {
        type: window.type,
        progress: (clamped - window.start) / (window.end - window.start),
      }
    }
  }
  return {
    type: last.type,
    progress: (clamped - last.start) / (last.end - last.start),
  }
}

function smoothstep(value: number): number {
  return value * value * (3 - 2 * value)
}

/** Resolves a continuous, globally progressing crossfade between modes. */
export function sampleAnimationTimeline(
  windows: readonly AnimationSegmentWindow[],
  progress: number,
): AnimationTimelineSample {
  const last = windows[windows.length - 1]
  if (last === undefined) {
    throw new RangeError('Animation segment timeline requires at least one window')
  }
  const clamped = Math.max(0, Math.min(1, progress))
  let activeIndex = windows.length - 1
  for (let index = 0; index < windows.length; index += 1) {
    const window = windows[index]
    if (window !== undefined && clamped < window.end) {
      activeIndex = index
      break
    }
  }
  const active = windows[activeIndex] ?? last
  const previous = windows[Math.max(0, activeIndex - 1)] ?? active
  if (activeIndex === 0 || active.type === previous.type) {
    return { fromType: active.type, toType: active.type, blend: 0, progress: clamped }
  }
  const local = Math.max(0, Math.min(1, (clamped - active.start) / (active.end - active.start)))
  return {
    fromType: previous.type,
    toType: active.type,
    blend: smoothstep(local),
    progress: clamped,
  }
}
